import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { errorMessage } from '@/shared/api/http';
import { BrandLogo } from '@/shared/components/BrandLogo';
import { authApi } from '../api/auth';
import { LoginAside } from '../components/LoginAside';
import { useSession } from '../hooks/useSession';

const schema = z.object({ email: z.email('Enter a valid email'), password: z.string().min(8, 'At least 8 characters') });
type Form = z.infer<typeof schema>;

export function LoginPage() {
  const { setUser } = useSession();
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: string } };
  const [error, setError] = useState('');
  const { register, handleSubmit, formState } = useForm<Form>({ resolver: zodResolver(schema) });

  const onSubmit = async (values: Form) => {
    setError('');
    try {
      setUser(await authApi.login(values.email, values.password));
      navigate(location.state?.from ?? '/', { replace: true });
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  return (
    <Box sx={{ minHeight: '100dvh', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' }, bgcolor: 'background.default' }}>
      <LoginAside />
      <Box component="main" sx={{ display: 'grid', placeItems: 'center', p: { xs: 2, sm: 4 } }}>
        <Box sx={{ width: '100%', maxWidth: 380 }}>
          <Box sx={{ display: { md: 'none' }, mb: 4 }}><BrandLogo height={28} /></Box>
          <Typography variant="h1" component="h1">Sign in to Screenline</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1, mb: 4 }}>Use the work email your admin invited.</Typography>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Stack sx={{ gap: 2 }}>
              <TextField label="Work email" type="email" autoComplete="username" {...register('email')} error={!!formState.errors.email} helperText={formState.errors.email?.message} />
              <TextField label="Password" type="password" autoComplete="current-password" {...register('password')} error={!!formState.errors.password} helperText={formState.errors.password?.message} />
              {error && <Alert severity="error" role="alert">{error}</Alert>}
              <Button type="submit" variant="contained" size="large" disabled={formState.isSubmitting} sx={{ mt: 1 }}>{formState.isSubmitting ? 'Signing in' : 'Sign in'}</Button>
            </Stack>
          </form>
        </Box>
      </Box>
    </Box>
  );
}
